/**
 * shadcn/ui Button — neutral theme (no brand chrome).
 *
 * Copied from `my.x9.app` unchanged so the two portals stay identical. The
 * brand CTAs on the mobile execute surfaces are a different thing entirely and
 * keep their own `.btn-paper-*` / `.btn-marine-*` classes.
 */
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:opacity-90',
        secondary:
          'border border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        /** Notion cyan tag pair. The reusable add control is a plus in these colours. */
        cyan: 'bg-[var(--badge-cyan)] text-[var(--badge-cyan-foreground)] hover:opacity-90',
        link: 'text-foreground underline-offset-4 hover:underline',
      },
      /*
       * `min-w` matches the height so a button never renders narrower than it
       * is tall. It also repairs `size="icon"` next to `width="auto"`: the two
       * variants both set a width, and `w-auto` wins in the cascade, which
       * used to collapse an icon button to the glyph's width.
       */
      size: {
        default: 'h-10 min-w-10 px-4 py-2',
        sm: 'h-9 min-w-9 rounded-md px-3',
        lg: 'h-11 min-w-11 rounded-md px-8',
        icon: 'h-10 w-10 min-w-10 shrink-0',
      },
      width: {
        full: 'w-full',
        auto: 'w-auto',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      width: 'full',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, width, type = 'button', ...props }, ref) => (
    <button
      type={type}
      className={cn(buttonVariants({ variant, size, width }), className)}
      ref={ref}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

export { Button, buttonVariants };
